/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Component } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { get } from 'lodash';
import { Alert, Icon } from 'antd';
import { userIsLoggedIn, getDefaultLanding } from '../actions/utils';
import { getReconDocument } from '../_shared/services/orders.service';
import { OrderModal } from '../dashboard/orders/orders.component';

// Card #283 — landing page for the QR code printed on the Recon Form.
//
// The scan flow is: scan -> /recon/:id -> (login if there is no session) ->
// /recon/:id -> the reconciliation's GPS order. This page holds no recon data
// of its own; it asks GET /api/orders/recon/:id what the form points at and
// opens it.
//
// Round 3 (Joel 2026-09-08) — the scan used to open the reconciliation PDF and
// must now open the associated order instead, "handled similar to a normal
// delivery ... so they can update the delivery status, complete the Recon Form
// delivery workflow and capture the recipient signature". The QR itself is
// unchanged: it still encodes /recon/<reconId>, because the Reconciliation
// Return order does not exist yet when the PDF is generated (Export Rec inserts
// the recon row, prints the form, and only then opens the Create Order modal),
// so the order id cannot be baked into the document. Keeping /recon/:id as the
// indirection also means every form already printed — including the ones in the
// field today — starts opening the order without being regenerated.
//
// The order is opened in OrderModal, the same component and the same
// { cashReturn, editOrder } shape the Reconciliations list uses for this very
// order (dashboard/recon-list/index.js -> selectOrder). That matters: the
// Reconciliation Return form — the trimmed field set, the Return Date / Return
// Route / Return Route Driver labels, and the Customer Signature pad while the
// order is still Order Approved — is all gated on the order screen's
// `cashReturn` flag, which it takes from the state handed to it and never
// recomputes from the order it fetches. Routing through a list screen's `?id=`
// deep link instead skips that state: the modal briefly mounts the ordinary
// delivery form, then `setFieldsOriginal` -> `handleSelectChange` self-corrects
// it to the Reconciliation Return form about 500ms later. Passing the flag
// still avoids that wrong-form flash and puts the order id in the modal title
// immediately.
//
// "Signed in" is `userIsLoggedIn(sState)` (= sState.user.id), NOT the presence
// of a token: app/src/config/setup.js mints an anonymous `temp@temp.com` JWT
// into user.token for every visitor before Navigation renders, so user.token is
// always populated and a token check here would pass for a logged-out scan.
// The endpoint enforces the same thing server-side — it rejects the anonymous
// identity and scopes the recon to the caller's licence family — so this gate
// is the UX half, not the security boundary.
//
// This page never navigates on its own when there is no session. GateWay owns
// that redirect (it pushes /login carrying `redirectTo` so the user lands back
// here after signing in) and reads history.location in its own
// componentDidMount, which runs AFTER this component's; navigating from here
// first would leave GateWay looking at /login and clobber the redirectTo state
// it sets. The message below is the fallback for the case GateWay does not
// cover — reaching this route without a fresh page load.
const styles = {
  container: `
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    padding: 24px;
    text-align: center;
  `,
  message: `
    margin-top: 16px;
    font-size: 16px;
  `,
};

// The order types the order screen treats as a return rather than a delivery.
// Mirrors dashboard/recon-list/index.js and dashboard/schedule.
const RETURN_TYPES = ['cash_return', 'cash_pickup', 'rej_return'];

export class ReconComponent extends Component {
  state = { error: null, editModal: null };

  componentDidMount = async () => {
    const reconId = get(this.props, 'match.params.id');

    if (!reconId) {
      return this.setState({ error: 'That link is missing a reconciliation ID.' });
    }

    if (!userIsLoggedIn(this.props.sState)) {
      return this.setState({ error: 'Please sign in to view this reconciliation.' });
    }

    // GateWay redirects a logged-out visitor to /login while this request is in
    // flight, which unmounts this component. Without cancelling, a late resolve
    // still opened the target and pulled the browser off the login screen.
    // axios 0.19 has no AbortController support, so this is CancelToken.
    this.cancelSource = axios.CancelToken.source();

    try {
      const doc = await getReconDocument(reconId, { cancelToken: this.cancelSource.token });

      if (this.unmounted) {
        return;
      }

      const orderDbId = get(doc, 'orderDbId');

      if (!orderDbId) {
        // A real reconciliation the caller may see, but nothing to deliver:
        // recons created before card #285 can be orphaned, with no
        // Reconciliation Return order ever submitted. Say so rather than
        // falling back to the PDF, which is the behaviour this round removed.
        return this.setState({
          error: 'No Reconciliation Return order is linked to this form yet.',
        });
      }

      const orderType = get(doc, 'orderType');

      this.setState({
        editModal: {
          isModal: true,
          state: {
            // The only order this endpoint ever resolves is the recon's
            // Reconciliation Return order, so an order row that predates
            // orderType still means the return form, not a delivery.
            cashReturn: !orderType || RETURN_TYPES.indexOf(orderType) > -1,
            // Identifiers only — the order screen re-reads the order from the
            // server by id, exactly as the Reconciliations list does.
            editOrder: { id: orderDbId, orderId: get(doc, 'orderId') },
          },
        },
      });
    } catch (err) {
      if (axios.isCancel(err) || this.unmounted) {
        return;
      }

      // Mapped by status only. The server's error strings are never rendered:
      // a DB failure used to arrive here as the raw SQL statement and was
      // painted straight into the Alert.
      const status = get(err, 'response.status');
      const message = status === 404
        ? 'We could not find that reconciliation.'
        : status === 401 || status === 403
          ? 'You do not have access to this reconciliation.'
          : 'We could not open that reconciliation.';

      this.setState({ error: message });
    }
  };

  componentWillUnmount = () => {
    this.unmounted = true;

    if (this.cancelSource) {
      this.cancelSource.cancel('Recon page unmounted');
    }
  };

  // Submit, Cancel Order, Delete Order and the modal's own close all arrive
  // here — the order screen routes every one of them through onClose while
  // isModal is set. This route is not part of the dashboard, so there is no
  // screen behind the modal to return to; put the user on their normal landing
  // instead of an empty page. replace() so Back does not re-enter the resolver.
  closeOrder = () => {
    const role = get(this.props, 'sState.user.role');
    this.props.history.replace(getDefaultLanding(role));
  };

  render() {
    const { error, editModal } = this.state;

    if (editModal) {
      return <OrderModal editModal={editModal} onClose={this.closeOrder} />;
    }

    return (
      <div className="Recon" css={css(styles.container)}>
        {error ? (
          <Alert message={error} type="error" />
        ) : (
          <div>
            <Icon type="loading" style={{ fontSize: 28 }} />
            <p css={css(styles.message)}>Opening reconciliation…</p>
          </div>
        )}
      </div>
    );
  }
}

export default connect(sState => ({ sState }))(ReconComponent);
